/**
 * Permission vocabulary of the Dashboard editor.
 *
 * The feature declares its own permissions here instead of importing the global
 * `PERMISSIONS` catalog, so every access rule of the section is readable — and
 * changeable — in one place inside the feature (same convention as
 * `agent-skills/permissions.ts`).
 */
export const DASHBOARDS_PERMISSIONS = {
  /** See the section and open a dashboard. */
  view: 'dashboard.view',
  /** Create a dashboard (and, per the PRD, manage its sources). */
  create: 'dashboard.create',
  /**
   * Edit a saved dashboard's canvas — widgets, groups, pages.
   *
   * Its own verb because the PRD keeps Ver and Editar apart: someone may open a
   * dashboard and read its figures without being able to change it, which is
   * what `dashboards/:id` vs `dashboards/:id/editor` encodes.
   */
  edit: 'dashboard.edit',
  /**
   * Download the pages of a saved dashboard as a PDF (SL-1938).
   *
   * Its own verb because the PRD keeps it apart from `view`: someone may read and
   * filter a dashboard without being able to hand it out as a file. The button
   * stays visible and disabled without it, so the reader learns who can export.
   */
  export: 'dashboard.export',
  /**
   * Rename, move and delete a saved dashboard.
   *
   * Three verbs and not one, because the role matrix separates them from
   * `edit`: `scripts/migrations/role-permissions.json:172-176` gives a
   * supervisor `edit` and `create` but withholds all three of these, and the
   * backend enforces `dashboard.delete` on its own handler
   * (`deleteDashboard.ts:27`). Gating any of them on `edit` or `create` would
   * offer a supervisor an action the server answers with a 403.
   */
  rename: 'dashboard.rename',
  move: 'dashboard.move',
  delete: 'dashboard.delete',
  /**
   * Declared for completeness and deliberately unused.
   *
   * The verb exists in the catalog, but the Access module cannot store a grant
   * against a dashboard: `Access/domain/types/access.types.ts:8` enumerates
   * `ObjectType` as folder/agent/table. Until that changes, the Home card shows
   * no Share button — see the note on `objectPerms.dashboard` in HomePage.
   */
  shareAccess: 'dashboard.share_access',
} as const;

/** Any permission string of this module. */
export type DashboardsPermission =
  (typeof DASHBOARDS_PERMISSIONS)[keyof typeof DASHBOARDS_PERMISSIONS];

/**
 * The roles the disabled Export PDF button names in its tooltip. The matrix
 * grants the verb to more (superadmin, superuser, implementador) but those are
 * not roles a reader would ask for; `permissions.test.ts` keeps this list
 * inside `role-permissions.json`.
 */
export const EXPORT_ROLES: readonly string[] = ['Admin', 'Supervisor'];
